import { CURRENCY_CODE } from '../../constants/currencyCodes';

// ISO 3166-1 alpha-2
export enum CountryIsoCodes {
    UK = 'gb',
    GB = 'gb',
    FR = 'fr',
    EN = 'en',
    PL = 'pl',
    ES = 'es',
    IT = 'it',
    PT = 'pt',
    ZA = 'za',
}

// ISO 639-1 standard language codes
export enum LanguageISOCodes {
    SPANISH = 'es-ES',
    FRENCH = 'fr-FR',
    ENGLISH_GB = 'en-GB',
    POLISH = 'pl-PL',
    ITALIAN = 'it-IT',
    PORTUGUESE = 'pt-PT',
}

export const languages: { [key: string]: { language: string; ISOCode: string } } = {
    SPANISH: {
        language: 'Spanish',
        ISOCode: LanguageISOCodes.SPANISH,
    },
    ENGLISH_UK: {
        language: 'English (UK)',
        ISOCode: LanguageISOCodes.ENGLISH_GB,
    },
    FRENCH: {
        language: 'French',
        ISOCode: LanguageISOCodes.FRENCH,
    },
    POLISH: {
        language: 'Polish',
        ISOCode: LanguageISOCodes.POLISH,
    },
    ITALIAN: {
        language: 'Italian',
        ISOCode: LanguageISOCodes.ITALIAN,
    },
    PORTUGUESE: {
        language: 'Portuguese',
        ISOCode: LanguageISOCodes.PORTUGUESE,
    },
};

export default class Country {
    public readonly code: string;

    constructor(
        public readonly value: string,
        public readonly countryName: string,
        public readonly language: string,
        public readonly timezone: string,
        public readonly label: string, // Language name...
        public readonly languageISOCode: string,
        public readonly currencyCode: string,
        public readonly countryIsoCode: string,
    ) {
        this.code = value;
    }
}

export const countries: Country[] = [
    new Country(
        'UK',
        'United Kingdom',
        'en',
        'Europe/London',
        languages.ENGLISH_UK.language,
        languages.ENGLISH_UK.ISOCode,
        CURRENCY_CODE.GBP,
        CountryIsoCodes.GB,
    ),
    new Country(
        'FR',
        'France',
        'fr',
        'Europe/Paris',
        languages.FRENCH.language,
        languages.ENGLISH_UK.ISOCode,
        CURRENCY_CODE.EUR,
        CountryIsoCodes.FR,
    ),
    new Country(
        'ES',
        'Spain',
        'es',
        'Europe/Madrid',
        languages.SPANISH.language,
        languages.ENGLISH_UK.ISOCode,
        CURRENCY_CODE.EUR,
        CountryIsoCodes.ES,
    ),
    new Country(
        'PL',
        'Poland',
        'pl',
        'Europe/Warsaw',
        languages.POLISH.language,
        languages.ENGLISH_UK.ISOCode,
        CURRENCY_CODE.PLN,
        CountryIsoCodes.PL,
    ),
    new Country(
        'IT',
        'Italy',
        'it',
        'Europe/Rome',
        languages.ITALIAN.language,
        languages.ENGLISH_UK.ISOCode,
        CURRENCY_CODE.EUR,
        CountryIsoCodes.IT,
    ),
    new Country(
        'PT',
        'Portugal',
        'pt',
        'Europe/Lisbon',
        languages.PORTUGUESE.language,
        languages.ENGLISH_UK.ISOCode,
        CURRENCY_CODE.EUR,
        CountryIsoCodes.PT,
    ),
    new Country(
        'ZA',
        'South Africa',
        'en',
        'Africa/Johannesburg',
        languages.ENGLISH_UK.language,
        languages.ENGLISH_UK.ISOCode,
        CURRENCY_CODE.ZAR,
        CountryIsoCodes.ZA,
    ),
];

export const DEFAULT_COUNTRY: Country = Object.freeze(countries[0]);
export const LOCAL_STORAGE_LANG_KEY = 'selected-country-code';

function getCountryBy<P extends keyof Country>(
    prop: P,
    value: Country[P] | null | undefined,
    baseCountries: Country[],
): Country {
    return baseCountries.filter((item: any) => item[prop] === value)?.[0] || DEFAULT_COUNTRY;
}

export function getCountryFromValue(value: string | null | undefined, baseCountries: Country[]): Country {
    return getCountryBy('value', value, baseCountries);
}

export function getCountryFromIsoCode(value: CountryIsoCodes | null | undefined, baseCountries: Country[]): Country {
    return getCountryBy('countryIsoCode', value, baseCountries);
}

export function getDefaultCountry(baseCountries: Country[]): Country {
    let defaultCountry = DEFAULT_COUNTRY;

    // Get default language from navigator locale
    if (typeof window !== 'undefined') {
        const locale = window?.navigator?.language;
        if (locale) {
            const language = locale?.split('-')?.[0];
            defaultCountry = getCountryBy('language', language, baseCountries);
        }
        // If a language was saved, use this one instead
        const savedCountryCode = window?.localStorage?.getItem(LOCAL_STORAGE_LANG_KEY);
        if (savedCountryCode) {
            defaultCountry = getCountryFromValue(savedCountryCode, baseCountries);
        }
    }

    return defaultCountry;
}
